import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { api } from '../services';
import { ENDPOINTS } from '../services/endpoints';

const STORAGE_QUOTA_ERROR_FRAGMENT = 'Storage quota exceeded';

const extractUploadErrorMessage = (errorOrResponse) => (
  errorOrResponse?.response?.data?.msg ||
  errorOrResponse?.response?.data?.message ||
  errorOrResponse?.response?.data?.error ||
  errorOrResponse?.response?.data?.data?.msg ||
  errorOrResponse?.response?.data?.data?.message ||
  errorOrResponse?.response?.data?.data?.error ||
  errorOrResponse?.msg ||
  errorOrResponse?.message ||
  errorOrResponse?.error ||
  null
);

export const isStorageQuotaExceededError = (error) => {
  const message = typeof error === 'string' ? error : extractUploadErrorMessage(error);
  return typeof message === 'string' && message.includes(STORAGE_QUOTA_ERROR_FRAGMENT);
};

// Get pre-signed URL for file upload
export const getPreSignedUrl = createAsyncThunk('file/getPreSignedUrl', async (fileData, { rejectWithValue }) => {
  try {
    const response = await api.post(ENDPOINTS.FILE_PRE_UPLOAD, {
      file_name: fileData.name,
      content_type: fileData.type,
      file_size: fileData.size,
      file_category: 'attachments'
    });

    if (response?.data?.status === false) {
      const message = extractUploadErrorMessage(response.data);
      if (isStorageQuotaExceededError(message)) {
        toast.error(message, { toastId: 'storage-quota-exceeded' });
      }
      return rejectWithValue(message);
    }

    return response.data.data;
  } catch (error) {
    const message = extractUploadErrorMessage(error);
    if (isStorageQuotaExceededError(message)) {
      toast.error(message, { toastId: 'storage-quota-exceeded' });
    }
    return rejectWithValue(message);
  }
});

// Confirm file upload
export const confirmFileUpload = createAsyncThunk('file/confirmFileUpload', async (fileId, { rejectWithValue }) => {
  try {
    const response = await api.post(ENDPOINTS.FILE_CONFIRM_UPLOAD, { file_id: fileId });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

// Get file details
export const getFileDetails = createAsyncThunk('file/getFileDetails', async (fileId, { rejectWithValue }) => {
  try {
    const response = await api.get(ENDPOINTS.FILE_DETAILS(fileId));
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

// Complete file upload process
export const uploadFile = createAsyncThunk('file/uploadFile', async (file, { dispatch, rejectWithValue }) => {
  try {
    // Step 1: Get pre-signed URL
    const preSignedData = await dispatch(getPreSignedUrl(file)).unwrap();
    
    // Step 2: Upload file to presigned URL
    await fetch(preSignedData.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
        'x-ms-blob-type': 'BlockBlob'
      }
    });
    
    // Step 3: Confirm upload
    const fileResponse = await dispatch(confirmFileUpload(preSignedData.file_id)).unwrap();
    
    return {
      key: fileResponse.data.file_data.key,
      url: fileResponse.data.file_data.access_url,
      name: fileResponse.data.file_data.original_file_name
    };
  } catch (error) {
    return rejectWithValue(error);
  }
});
